import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import utils from 'utils';
import ANALYTICS from 'constants/analytics';
import { SCROLL } from 'constants';
import FlyoutTabs from 'components/FlyoutTabs';
import Button from 'components/Button';
import MessageBanner from 'components/MessageBanner';
import GenericGboCopyModal from 'components/modals/GenericGboCopyModalContent';
import RecommendedExtrasAlertModal from 'components/modals/RecommendedExtrasAlertModal';
import InPathVehicleUpgradeExtras from 'components/ReservationFlow/InPathVehicleUpgrade/InPathVehicleUpgradeExtras';
import CurrencyConversionModal from 'components/modals/CurrencyConversionModal';
import BundlesDetailsModal from 'components/modals/BundlesDetailsModal';
import ExtrasHeader from './ExtrasHeader';
import Bundles from './Bundles';
import IndividualExtras from './IndividualExtras';
import './styles/extras-select.scss';

/**
 * component for Extras Page
 *
 * @param {object}    props.vehicleRate - Selected car rates
 */

class Extras extends Component {
  state = {
    numberOfIndividualExtrasLoading: 0,
  };

  scrollRef = React.createRef();

  handleClickContinue = () => {
    if (this.props.showRecommendedExtrasAlertModal) {
      this.props.openRecommendedExtrasAlertModal();
    } else {
      this.props.extrasComplete();
    }
  };

  componentDidMount() {
    const { genericTitle } = this.props;
    document.title = `${utils.i18n('extras_main_title')} - ${genericTitle}`;
  }

  componentDidUpdate(prevProps) {
    const { preSelectedExtras, setAllExtras } = this.props;
    if (utils.gmi.isArrayNotEmpty(preSelectedExtras) && preSelectedExtras !== prevProps.preSelectedExtras) {
      setAllExtras(preSelectedExtras);
    }
  }

  // current place of ref it should go - heigh of progress header (this value is okay on mobile too)
  scrollToRef = () => window.scrollTo(0, this.scrollRef.current.offsetTop - SCROLL.HEIGHT_OF_PROGRESS_BAR_DESKTOP);

  toggleExtrasLoading = (extraLoadingBool) => {
    //  if numberOfIndividualExtrasLoading === 0, we know extras are not loading
    this.setState(({ numberOfIndividualExtrasLoading }) => {
      const newExtraLoadingValue = extraLoadingBool
        ? numberOfIndividualExtrasLoading + 1
        : numberOfIndividualExtrasLoading - 1;
      return {
        numberOfIndividualExtrasLoading: newExtraLoadingValue,
      };
    });
  };

  render() {
    const {
      hasExtras,
      includedExtrasAndDrivers,
      showBundles,
      vehicleRate,
      showConvertedTotalsModal,
      numberOfExtrasSelected,
      numberOfIncludedExtras,
      selectedBundle,
    } = this.props;
    const { numberOfIndividualExtrasLoading } = this.state;
    const areIndividualExtrasLoading = numberOfIndividualExtrasLoading !== 0;
    const displayCarClass = includedExtrasAndDrivers?.length < 4;
    const estimatedTotal = vehicleRate?.price_summary?.estimated_total_view;
    const estimatedTotalPayment = vehicleRate?.price_summary?.estimated_total_payment;
    const isForeignCurrency = estimatedTotal?.code !== estimatedTotalPayment?.code;

    const haveNoAddedExtras = numberOfExtrasSelected === 0 || numberOfExtrasSelected === numberOfIncludedExtras;
    const isSkipExtrasPillBtn = hasExtras && haveNoAddedExtras && !selectedBundle?.name;
    const buttonClass = cn(
      {
        pill: isSkipExtrasPillBtn,
      },
      'button'
    );

    const dataDtmTrack = utils.analytics.dtm(
      ANALYTICS.UI_BUTTON,
      ANALYTICS.EXTRAS,
      isSkipExtrasPillBtn ? ANALYTICS.SKIP : ANALYTICS.CONTINUE
    );

    const i18nButtonText = isSkipExtrasPillBtn
      ? 'extras_continue_review_cta_no_extras_added'
      : 'extras_continue_review_cta_extras_selected';

    return (
      <div className='row'>
        <div className='extras-select'>
          <ExtrasHeader
            onClickContinue={this.handleClickContinue}
            hasExtras={hasExtras}
            individualExtraLoading={areIndividualExtrasLoading}
            scrollToRef={this.scrollToRef}
            isForeignCurrency={isForeignCurrency}
            showConvertedTotalsModal={showConvertedTotalsModal}
          />

          <InPathVehicleUpgradeExtras />

          {!hasExtras ? (
            <MessageBanner
              title={utils.i18n('extras_no_extras_available_title')}
              message={utils.i18n('extras_no_extras_available_message')}
            />
          ) : (
            <>
              {includedExtrasAndDrivers.length > 0 && (
                <div
                  className={cn('extras-select__included-section', {
                    'extras-select__included-section--no-bundles': !showBundles,
                  })}
                >
                  <h2 className='extras-select__included-heading'>
                    {utils.i18n('extras_included_in_your_reservation')}
                  </h2>
                  <FlyoutTabs
                    items={includedExtrasAndDrivers}
                    className='included-extras'
                    contentKey={'detailed_description'}
                    fullWidth
                    icon='extra'
                    hasClearBelow={displayCarClass}
                    isExtrasDrawer
                  />
                </div>
              )}
              {showBundles && (
                <Bundles
                  displayCarClass={displayCarClass}
                  scrollToRef={this.scrollToRef}
                  hasClearAbove={!includedExtrasAndDrivers.length}
                />
              )}
              <IndividualExtras
                shouldShowTitle={showBundles}
                toggleExtrasLoading={this.toggleExtrasLoading}
                scrollRef={this.scrollRef}
              />
              <div className='extras-footer'>
                <Button
                  className={buttonClass}
                  loading={areIndividualExtrasLoading}
                  disabled={areIndividualExtrasLoading}
                  onClick={this.handleClickContinue}
                  data-dtm-track={dataDtmTrack}
                >
                  {utils.i18n(i18nButtonText)}
                </Button>
              </div>
            </>
          )}
          <BundlesDetailsModal />

          <GenericGboCopyModal />
          <RecommendedExtrasAlertModal />
          <CurrencyConversionModal />
        </div>
      </div>
    );
  }
}

Extras.propTypes = {
  hasExtras: PropTypes.bool,
  includedExtrasAndDrivers: PropTypes.array,
  showBundles: PropTypes.bool,
  showRecommendedExtrasAlertModal: PropTypes.bool,
  preSelectedExtras: PropTypes.array,
  openRecommendedExtrasAlertModal: PropTypes.func,
  extrasComplete: PropTypes.func,
  setAllExtras: PropTypes.func,
  vehicleRate: PropTypes.object,
  showConvertedTotalsModal: PropTypes.func,
};

export default Extras;
